import React, { useEffect, useState } from "react";
import moment from "moment";
import Summary from "../components/Summary";
import Select from "react-select";
import ToggleTable from "../components/Tables/Fleet/ToggleTable";
import BottomToggleTable from "../components/Tables/Fleet/BottomToggleTable";
import LoadingOverlay from "../components/LoadingOverlay";
import WeeklyDatePicker from "../../payments/paymentsComponents/WeeklyDatePicker";
import DateRangePicker from "../../payment_platform/DateRangePicker";
import { toDateObjs, toStrings } from "../components/helpers/paymentsHelper";
import {
  DownArrow,
  ToggleIcon,
  SearchIcon,
  RefreshIcon,
  DownloadIcon,
  ICON_GREY,
} from "../components/helpers/svgHelper";
import { IconButton, ActionButton, PP_REFRESH_GREY } from "../components/helpers/paymentControls";

/**
 * Formats a backend week label (e.g. "Jun 29") into an ordinal display label
 * (e.g. "29th Jun") for the Statement download buttons.
 *
 * @param {string} label - Week label in "MMM DD" format, as returned by the API.
 * @returns {string|null} The ordinal-formatted label, or null if not parseable.
 */
const formatOrdinalLabel = (label) => {
  if (!label) return null;
  const parsed = moment(label, "MMM DD");
  return parsed.isValid() ? parsed.format("Do MMM") : label;
};

function Fleet({
  data,
  fleets,
  companies,
  comparisonOptions,
  loading,
  handleChange,
  selectedFleet,
  setSelectedFleet,
  defaultSelectedFleet,
  selectedCompanies,
  setSelectedCompanies,
  fetchFleetOverview,
  selectedOption,
  setSelectedOption,
  selectStyles,
  multiSelectStyles,
  fleetTrips,
  fleetTotals,
  week,
  setWeek,
  setData,
  // downloadPdf
  downloadFleetStatement,
  paymentPlatformMvp,
}) {
  const [tabView, setTabView] = useState(true);
  const [dropdown, setDropdown] = useState(false);

  useEffect(() => {
    resetFilters();
  }, []);

  // Prefer the actual API-returned week labels once data has loaded; fall back
  // to a placeholder derived from the selected date range (current week / 7
  // days prior) so the Statement buttons are usable before the first fetch.
  const statementLabel = (weekStart, fallbackOffsetDays) => {
    if (weekStart) return formatOrdinalLabel(weekStart);
    if (!week?.from) return null;
    return moment(week.from, "DD/MM/YYYY").subtract(fallbackOffsetDays, "days").format("Do MMM");
  };
  const currentStatementLabel = statementLabel(data.current_week_start, 0);
  const previousStatementLabel = statementLabel(data.previous_week_start, 7);

  const fleetsSelect = () => {
    const formatedFleets = fleets.map((fleet) => {
      return { label: fleet.name, value: fleet.id };
    });
    return formatedFleets;
  };

  const companiesSelect = () => {
    const formatedCompanies = companies.map((company) => {
      return { label: company.name, value: company.id };
    });
    return formatedCompanies;
  };

  useEffect(() => {
    const allCompanies = companiesSelect();
    if (allCompanies.length > 0 && selectedCompanies.length === 0) {
      setSelectedCompanies(allCompanies);
    }
  }, [companiesSelect]);

  const onChangedSelectedFleet = (fleetSelected) => {
    setSelectedFleet(fleetSelected);
  };

  const allCompaniesOption = {
    label: "All",
    value: "all",
    companies: companiesSelect(),
  };

  const onChangedSelectedCompanies = (selected) => {
    if (!selected) return;

    if (
      Array.isArray(selected) &&
      selected.find((opt) => opt.value === "all")
    ) {
      // If "All" is selected, set all companies
      setSelectedCompanies(companiesSelect());
    } else {
      // Otherwise, set selected individual companies
      setSelectedCompanies(selected);
    }
  };

  const resetFilters = () => {
    setSelectedFleet(defaultSelectedFleet ?? null);
    setSelectedCompanies([]);
    setSelectedOption(null);
    // Clearing the filters must also clear the rendered overview — the tables are
    // gated on data.total_trips, so leaving the last fetch in place makes Reset
    // look like a no-op. Mirrors the Weekly tab's reset.
    setData([]);
  };

  // Under the MVP flag the comparison-period select is hidden, so Search can no
  // longer gate on selectedOption. Enable it once a real fleet is chosen instead.
  // The "__ALL__" placeholder is the default and does not count as a selection,
  // matching the Refresh button's own guard.
  const searchDisabled = paymentPlatformMvp
    ? !selectedFleet || selectedFleet.value === "__ALL__"
    : selectedOption === null;

  // Exact styling from the Payment Platform design (Figma node 3036:76468).
  // The design is exported at ~1.8537x, so each bracketed Figma value is divided
  // by 1.8537 to real px (e.g. field h-[76px] -> 41px, rounded-[9.268px] -> 5px,
  // text-[29.66px] -> 16px, gap-[18.537px] -> 10px). Only applied under the flag.
  const ubuntu = "'Ubuntu', system-ui, sans-serif";
  const mvpFleetSelectStyles = {
    ...selectStyles,
    control: (base, state) => ({
      ...base,
      minHeight: 41,
      height: 41,
      borderRadius: 5,
      border: "1px solid rgba(110, 108, 113, 0.3)",
      borderColor: state.isFocused ? "#745D96" : "rgba(110, 108, 113, 0.3)",
      boxShadow: "none",
      backgroundColor: "#FFFFFF",
      "&:hover": { borderColor: state.isFocused ? "#745D96" : "rgba(110, 108, 113, 0.3)" },
    }),
    valueContainer: (base) => ({ ...base, height: 41, padding: "0 10px" }),
    placeholder: (base) => ({ ...base, margin: 0, color: "#6E6C71", fontFamily: ubuntu, fontWeight: 300, fontSize: 16 }),
    singleValue: (base) => ({ ...base, color: "#6E6C71", fontFamily: ubuntu, fontWeight: 300, fontSize: 16 }),
    input: (base) => ({ ...base, margin: 0, padding: 0, color: "#6E6C71", fontFamily: ubuntu, fontWeight: 300, fontSize: 16 }),
    indicatorsContainer: (base) => ({ ...base, height: 41 }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({ ...base, color: "#6E6C71", paddingRight: 10 }),
    menu: (base) => ({ ...base, borderRadius: 5, overflow: "hidden", marginTop: 4, boxShadow: "0 0 10px 1px rgba(0, 0, 0, 0.15)" }),
    option: (base, state) => ({
      ...base,
      padding: "6px 15px",
      color: "#6E6C71",
      fontFamily: ubuntu,
      fontWeight: 300,
      fontSize: 16,
      backgroundColor: state.isFocused ? "#F5F5F5" : "#FFFFFF",
    }),
  };

  return (
    <div className="bg-transparent pt-2 pb-4 space-y-4">
      <LoadingOverlay loading={loading} />
      <div className="flex flex-row flex-wrap justify-between gap-y-2">
        <div
          className={`flex flex-row gap-2 ${
            paymentPlatformMvp
              ? "flex-nowrap items-center w-full"
              : "flex-wrap w-11/12"
          }`}
          style={{ gap: paymentPlatformMvp ? "10px" : undefined }}
        >
          <div
            className={paymentPlatformMvp ? "flex-1 min-w-0" : "w-2/5"}
            style={{ height: paymentPlatformMvp ? "41px" : "45px" }}
          >
            <Select
              value={selectedFleet}
              onChange={onChangedSelectedFleet}
              options={fleetsSelect()}
              placeholder="Select a fleet…"
              styles={paymentPlatformMvp ? mvpFleetSelectStyles : selectStyles}
            />
          </div>
          {!paymentPlatformMvp && (
            <div className="w-2/5" style={{ height: "40px" }}>
              <Select
                isMulti
                name="companies"
                options={[allCompaniesOption]}
                onChange={onChangedSelectedCompanies}
                className="basic-multi-select"
                classNamePrefix="select"
                placeholder={"Company"}
                closeMenuOnSelect={false}
                value={[allCompaniesOption]}
                styles={{
                  ...multiSelectStyles,
                  control: (base, state) => {
                    const inherited = typeof multiSelectStyles?.control === "function"
                      ? multiSelectStyles.control(base, state)
                      : base;
                    return { ...inherited, minHeight: 40, height: 40 };
                  },
                }}
              />
            </div>
          )}
          <div
            className={`relative text-left ${
              paymentPlatformMvp ? "flex-1 min-w-0" : "inline-block"
            }`}
          >
            {paymentPlatformMvp ? (
              <DateRangePicker
                value={toDateObjs(week)}
                onChange={(range) => setWeek(toStrings(range))}
                maxDate={new Date()}
                loading={loading}
                weekOnly
              />
            ) : (
              <>
                <div>
                  <button
                    type="button"
                    style={{ width: "150px" }}
                    onClick={() => setDropdown(!dropdown)}
                    className="inline-flex w-full justify-between gap-x-1.5 border-gray-400 border rounded-md bg-white text-gray-700 px-3 py-2 text-xxs font-thin shadow-sm"
                  >
                    {week.from} - {week.to}
                    <DownArrow />
                  </button>
                </div>
                {dropdown && (
                  <WeeklyDatePicker
                    setWeek={setWeek}
                    week={week}
                    dropdown={dropdown}
                    setDropdown={setDropdown}
                  />
                )}
              </>
            )}
          </div>
          {!paymentPlatformMvp && (
            <Select
              value={selectedOption}
              onChange={(option) => handleChange(option)}
              options={comparisonOptions}
              placeholder="Comparison Period"
              styles={selectStyles}
            />
          )}
          <div
            className={`flex items-center ${paymentPlatformMvp ? "" : "space-x-2"}`}
            style={{ gap: paymentPlatformMvp ? "10px" : undefined }}
          >
            {paymentPlatformMvp ? (
              <>
                <IconButton
                  title="Search"
                  testId="fleet-search-button"
                  disabled={searchDisabled}
                  onClick={fetchFleetOverview}
                  icon={<SearchIcon size={20} />}
                />
                <IconButton
                  title="Refresh"
                  testId="fleet-refresh-button"
                  disabled={!selectedFleet || selectedFleet.value === "__ALL__"}
                  onClick={() => resetFilters()}
                  fill={PP_REFRESH_GREY}
                  icon={<RefreshIcon size={20} color={ICON_GREY} />}
                />
              </>
            ) : (
              <>
                <button
                  data-testid="fleet-search-button"
                  disabled={searchDisabled}
                  onClick={fetchFleetOverview}
                  title="Search"
                  style={{ height: "40px", width: "40px", backgroundColor: searchDisabled ? "#C4C7CC" : "#745D96" }}
                  className="flex items-center justify-center rounded-lg"
                >
                  <SearchIcon size={16} />
                </button>
                <button
                  data-testid="fleet-refresh-button"
                  onClick={() => resetFilters()}
                  title="Refresh"
                  style={{ height: "40px", width: "40px", backgroundColor: "#9B999E" }}
                  className="flex items-center justify-center rounded-lg"
                >
                  <RefreshIcon size={16} color="white" />
                </button>
              </>
            )}
          </div>

          <div
            className={`flex items-center ${paymentPlatformMvp ? "" : "space-x-2"}`}
            style={{ gap: paymentPlatformMvp ? "10px" : undefined }}
          >
            {paymentPlatformMvp ? (
              <>
                <ActionButton onClick={() => downloadFleetStatement(1)} disabled={!currentStatementLabel}>
                  <DownloadIcon size={18} />
                  {currentStatementLabel ? `Statement ${currentStatementLabel}` : "Statement"}
                </ActionButton>
                <ActionButton onClick={() => downloadFleetStatement(2)} disabled={!previousStatementLabel}>
                  <DownloadIcon size={18} />
                  {previousStatementLabel ? `Statement ${previousStatementLabel}` : "Statement"}
                </ActionButton>
              </>
            ) : (
              <>
                <button
                  onClick={() => downloadFleetStatement(1)}
                  disabled={!currentStatementLabel}
                  style={{ backgroundColor: currentStatementLabel ? "#745D96" : "#C4C7CC" }}
                  className="flex items-center text-white whitespace-nowrap gap-1 font-normal text-sm px-4 rounded-lg"
                >
                  <DownloadIcon size={14} />
                  {currentStatementLabel ? `Statement ${currentStatementLabel}` : "Statement"}
                </button>
                <button
                  type="button"
                  onClick={() => downloadFleetStatement(2)}
                  disabled={!previousStatementLabel}
                  style={{ backgroundColor: previousStatementLabel ? "#745D96" : "#C4C7CC" }}
                  className="flex items-center text-white whitespace-nowrap gap-1 font-normal text-sm px-4 rounded-lg"
                >
                  <DownloadIcon size={14} />
                  {previousStatementLabel ? `Statement ${previousStatementLabel}` : "Statement"}
                </button>
              </>
            )}
          </div>
        </div>

        {!paymentPlatformMvp && (
          <div className="flex flex-row">
            <button
              disabled={true}
              onClick={() => setTabView(!tabView)}
              className="w-8 h-8 bg-gray-400 hover:bg-gray-700 rounded-md shadow-xl flex items-center justify-center transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-opacity-75"
            >
              <ToggleIcon />
            </button>
          </div>
        )}
      </div>

      {data.total_trips > 0 && (
        <>
          <div className="px-2 flex flex-row items-center" style={{ color: "#6E6C71" }}>
            {data.fleets_account_data[0]?.fleet_name} Week{" "}
            {data.current_week_start}
          </div>

          {/* Legacy (flag-off) top-of-page aggregators. The flag-on MVP hides them
              per the 2026-07-23 demo, so they are gated to the legacy path only. */}
          {!paymentPlatformMvp && (
            <div className="flex flex-row space-x-4">
              <div className="w-1/2">
                <Summary title="Trip Breakdown" breakdown={fleetTrips} />
              </div>
              <div className="w-1/2">
                <Summary title="Payout" breakdown={fleetTotals} />
              </div>
            </div>
          )}

          <div
            className="overflow-y-auto overflow-x-auto px-2 scrollbar mb-10"
            style={{ maxHeight: "800px" }}
          >
            <ToggleTable data={data.toggle_data} />
          </div>

          <div
            className="overflow-y-auto overflow-x-auto px-2 scrollbar mt-4"
            style={{ maxHeight: "800px" }}
          >
            <BottomToggleTable
              data={data.bottom_toggle}
              selectStyles={selectStyles}
              current_week={data.current_week_start}
              previous_week={data.previous_week_start}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default Fleet;
